import * as account from "./account";
import * as allocations from "./allocations";
import * as preferences from "./preferences";
import * as permissions from "./permissions";
import * as planzone from "./planzone";
import * as projectExternalLinks from "./projectExternalLinks";
import * as projectManagementPlan from "./projectManagementPlan";
import * as projectReports from "./projectReports";
import * as projects from "./projects";
import * as projectStatuses from "./projectStatuses";
import * as resources from "./resources";
import * as resourceGroups from "./resourceGroups";
import * as tags from "./tags";
import * as zones from "./zones";
import * as comments from "./comments";
import * as projectPortfolio from "./projectPortfolio";
import * as notifications from "./notifications";
import * as documents from "./documents";
import * as search from "./search";
import * as timeSheets from "./timeSheets";
import * as budget from "./budget";
import * as workLoads from "./workLoads";
import * as constraints from "./constraints";
import * as unavailability from "./unavailability";
import * as dataExports from "./dataExports";
import * as strategicAxes from "./strategicAxes";
import * as videoConferenceCall from "./videoConferenceCall";
import * as calendarExportLink from "./calendarExportLink";
import * as secondaryCriteria from "./secondaryCriteria";
import * as calendarHolidays from "./calendarHolidays";
import * as logs from "./logs";
import * as capacityPlanning from "./capacityPlanning";
import * as chatbot from "./chatbot";
import * as organizationalUnits from "./organizationalUnits";

const UseCases = {
  ...account.UseCases,
  ...allocations.UseCases,
  ...preferences.UseCases,
  ...permissions.UseCases,
  ...planzone.UseCases,
  ...projectExternalLinks.UseCases,
  ...projectManagementPlan.UseCases,
  ...projectReports.UseCases,
  ...projects.UseCases,
  ...projectStatuses.UseCases,
  ...resources.UseCases,
  ...resourceGroups.UseCases,
  ...tags.UseCases,
  ...zones.UseCases,
  ...comments.UseCases,
  ...projectPortfolio.UseCases,
  ...notifications.UseCases,
  ...documents.UseCases,
  ...search.UseCases,
  ...timeSheets.UseCases,
  ...budget.UseCases,
  ...workLoads.UseCases,
  ...constraints.UseCases,
  ...unavailability.UseCases,
  ...dataExports.UseCases,
  ...strategicAxes.UseCases,
  ...videoConferenceCall.UseCases,
  ...calendarExportLink.UseCases,
  ...secondaryCriteria.UseCases,
  ...calendarHolidays.UseCases,
  ...logs.UseCases,
  ...capacityPlanning.UseCases,
  ...chatbot.UseCases,
  ...organizationalUnits.UseCases,
};

type UseCases =
  | account.UseCases
  | allocations.UseCases
  | preferences.UseCases
  | permissions.UseCases
  | planzone.UseCases
  | projectExternalLinks.UseCases
  | projectManagementPlan.UseCases
  | projectReports.UseCases
  | projects.UseCases
  | projectStatuses.UseCases
  | resources.UseCases
  | resourceGroups.UseCases
  | tags.UseCases
  | zones.UseCases
  | comments.UseCases
  | projectPortfolio.UseCases
  | notifications.UseCases
  | documents.UseCases
  | search.UseCases
  | timeSheets.UseCases
  | budget.UseCases
  | workLoads.UseCases
  | constraints.UseCases
  | unavailability.UseCases
  | dataExports.UseCases
  | strategicAxes.UseCases
  | videoConferenceCall.UseCases
  | calendarExportLink.UseCases
  | secondaryCriteria.UseCases
  | calendarHolidays.UseCases
  | logs.UseCases
  | capacityPlanning.UseCases
  | chatbot.UseCases
  | organizationalUnits.UseCases;

type Boundaries = account.Boundaries &
  allocations.Boundaries &
  preferences.Boundaries &
  permissions.Boundaries &
  planzone.Boundaries &
  projectExternalLinks.Boundaries &
  projectManagementPlan.Boundaries &
  projectReports.Boundaries &
  projects.Boundaries &
  projectStatuses.Boundaries &
  resources.Boundaries &
  resourceGroups.Boundaries &
  tags.Boundaries &
  zones.Boundaries &
  comments.Boundaries &
  projectPortfolio.Boundaries &
  notifications.Boundaries &
  documents.Boundaries &
  search.Boundaries &
  timeSheets.Boundaries &
  budget.Boundaries &
  workLoads.Boundaries &
  constraints.Boundaries &
  unavailability.Boundaries &
  dataExports.Boundaries &
  strategicAxes.Boundaries &
  videoConferenceCall.Boundaries &
  calendarExportLink.Boundaries &
  secondaryCriteria.Boundaries &
  calendarHolidays.Boundaries &
  logs.Boundaries &
  capacityPlanning.Boundaries &
  chatbot.Boundaries &
  organizationalUnits.Boundaries;

type Core = account.Core &
  allocations.Core &
  preferences.Core &
  permissions.Core &
  planzone.Core &
  projectExternalLinks.Core &
  projectManagementPlan.Core &
  projectReports.Core &
  projects.Core &
  projectStatuses.Core &
  resources.Core &
  resourceGroups.Core &
  tags.Core &
  zones.Core &
  comments.Core &
  projectPortfolio.Core &
  notifications.Core &
  documents.Core &
  search.Core &
  timeSheets.Core &
  budget.Core &
  workLoads.Core &
  constraints.Core &
  unavailability.Core &
  dataExports.Core &
  strategicAxes.Core &
  videoConferenceCall.Core &
  calendarExportLink.Core &
  secondaryCriteria.Core &
  calendarHolidays.Core &
  logs.Core &
  capacityPlanning.Core &
  chatbot.Core &
  organizationalUnits.Core;

type Dependencies = account.Dependencies &
  allocations.Dependencies &
  preferences.Dependencies &
  permissions.Dependencies &
  planzone.Dependencies &
  projectExternalLinks.Dependencies &
  projectManagementPlan.Dependencies &
  projectReports.Dependencies &
  projects.Dependencies &
  projectStatuses.Dependencies &
  resources.Dependencies &
  resourceGroups.Dependencies &
  tags.Dependencies &
  zones.Dependencies &
  comments.Dependencies &
  projectPortfolio.Dependencies &
  notifications.Dependencies &
  documents.Dependencies &
  search.Dependencies &
  timeSheets.Dependencies &
  budget.Dependencies &
  workLoads.Dependencies &
  constraints.Dependencies &
  unavailability.Dependencies &
  dataExports.Dependencies &
  strategicAxes.Dependencies &
  videoConferenceCall.Dependencies &
  calendarExportLink.Dependencies &
  secondaryCriteria.Dependencies &
  calendarHolidays.Dependencies &
  logs.Dependencies &
  capacityPlanning.Dependencies &
  chatbot.Dependencies &
  organizationalUnits.Dependencies;

function createCore(dependencies: Dependencies): Core {
  return {
    ...account.createCore(dependencies),
    ...allocations.createCore(dependencies),
    ...preferences.createCore(dependencies),
    ...permissions.createCore(dependencies),
    ...planzone.createCore(dependencies),
    ...projectExternalLinks.createCore(dependencies),
    ...projectManagementPlan.createCore(dependencies),
    ...projectReports.createCore(dependencies),
    ...projects.createCore(dependencies),
    ...projectStatuses.createCore(dependencies),
    ...resources.createCore(dependencies),
    ...resourceGroups.createCore(dependencies),
    ...tags.createCore(dependencies),
    ...zones.createCore(dependencies),
    ...comments.createCore(dependencies),
    ...projectPortfolio.createCore(dependencies),
    ...notifications.createCore(dependencies),
    ...documents.createCore(dependencies),
    ...search.createCore(dependencies),
    ...timeSheets.createCore(dependencies),
    ...budget.createCore(dependencies),
    ...workLoads.createCore(dependencies),
    ...constraints.createCore(dependencies),
    ...unavailability.createCore(dependencies),
    ...dataExports.createCore(dependencies),
    ...strategicAxes.createCore(dependencies),
    ...videoConferenceCall.createCore(dependencies),
    ...calendarExportLink.createCore(dependencies),
    ...secondaryCriteria.createCore(dependencies),
    ...calendarHolidays.createCore(dependencies),
    ...logs.createCore(dependencies),
    ...capacityPlanning.createCore(dependencies),
    ...chatbot.createCore(dependencies),
    ...organizationalUnits.createCore(dependencies),
  };
}

export type { Core, Boundaries, Dependencies };

export { UseCases, createCore };
